/**
 * MembershipEditForm.js — Manage-membership edit forms (kind
 * "membership-edit-form"). Common Commerce7 club feature.
 *
 * Parity with old CHECKOUT.membership.getFormContent's four sections
 * (renderDeliverySection / renderPaymentSection / renderFrequencySection /
 * renderCancelSection), each → c7UpdateMembership (or c7CancelMembership).
 *
 * c7UpdateMembership only accepts EXISTING saved address/card ids (not raw
 * new ones) for shipToCustomerAddressId/customerCreditCardId — same
 * saved-only constraint as the join flow's payment step (Commerce7's
 * add-address/add-card-to-customer endpoints are available via
 * api.c7AddAddress/c7AddCard if a saved list is empty, but adding a new card
 * needs a tokenized value this widget doesn't produce; adding a new address
 * has no such restriction, so "Add new address" is supported here).
 *
 * message.data: { type: "delivery"|"payment"|"frequency"|"cancel", membership }
 *
 * EVENTS OUT: ws-membership-update {membershipId, payload},
 *             ws-membership-cancel {membershipId, reason, comments}
 */

import { emit, getState } from "../../../core/state.js";
import * as api from "../../../core/api.js";

const CANCEL_REASONS = [
  "Too much wine",
  "Financial reasons",
  "Health reasons",
  "Pregnant",
  "Other",
];

function section(titleText) {
  const sec = document.createElement("div");
  sec.className = "ws-checkout-section";
  const head = document.createElement("div");
  head.className = "ws-checkout-section-head";
  const title = document.createElement("p");
  title.className = "ws-checkout-section-title";
  title.textContent = titleText;
  head.appendChild(title);
  sec.appendChild(head);
  const fields = document.createElement("div");
  fields.className = "ws-checkout-fields";
  sec.appendChild(fields);
  return { sec, fields };
}

function submitFooter(label) {
  const footer = document.createElement("div");
  footer.className = "ws-checkout-footer";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "ws-checkout-submit";
  submit.textContent = label;
  footer.appendChild(submit);
  return { footer, submit };
}

export default class MembershipEditForm {
  render(message) {
    const type = message?.data?.type;
    const membership = message?.data?.membership || {};
    if (type === "payment") return this._renderPayment(membership);
    if (type === "frequency") return this._renderFrequency(membership);
    if (type === "cancel") return this._renderCancel(membership);
    return this._renderDelivery(membership);
  }

  // Old parity: CHECKOUT.membership.renderDeliverySection showed the current
  // delivery info plus a "Change to Shipping"/"Change to Pickup" toggle that
  // revealed the OTHER type's options. Here both the saved shipping-address
  // list AND the pickup-location list are shown together (no toggle) — a
  // member can either re-pick within their current delivery type or switch
  // to the other one, whichever radio (in either list) was most recently
  // picked is what Save submits. Each list's card format (bold name/title,
  // then address/city/country-zip/phone lines) mirrors renderDeliverySection's
  // own ship-address and pickup-location cards.
  _renderDelivery(membership) {
    const isPickup = membership.orderDeliveryMethod === "Pickup";
    const form = document.createElement("form");
    form.className = "ws-checkout-form";
    const { sec, fields } = section("Update delivery");

    let activeType = isPickup ? "Pickup" : "Ship";
    let shipSelectedId = null;
    let pickupSelectedId = null;

    const shipHeading = document.createElement("p");
    shipHeading.className = "ws-checkout-section-title";
    shipHeading.style.fontSize = "14px";
    shipHeading.textContent = "Shipping Address";
    fields.appendChild(shipHeading);
    const shipList = document.createElement("div");
    shipList.className = "ws-checkout-fields";
    fields.appendChild(shipList);

    const pickupHeading = document.createElement("p");
    pickupHeading.className = "ws-checkout-section-title";
    pickupHeading.style.fontSize = "14px";
    pickupHeading.textContent = "Pickup Location";
    fields.appendChild(pickupHeading);
    const pickupList = document.createElement("div");
    pickupList.className = "ws-checkout-fields";
    fields.appendChild(pickupList);

    form.appendChild(sec);
    const { footer, submit } = submitFooter("Save");
    submit.disabled = true;
    form.appendChild(footer);

    function addressCard(nameLine, lines) {
      const info = document.createElement("div");
      const strong = document.createElement("strong");
      strong.textContent = nameLine;
      info.appendChild(strong);
      lines.filter(Boolean).forEach((text) => {
        info.appendChild(document.createElement("br"));
        info.appendChild(document.createTextNode(text));
      });
      return info;
    }

    (async () => {
      const customerId = getState().user?.customerId || "";
      let addresses = [];
      try {
        const res = await api.c7GetAddresses({
          customerId,
          customerToken: getState().user?.customerToken || "",
        });
        addresses = Array.isArray(res?.addresses) ? res.addresses : [];
      } catch {
        /* leave empty */
      }
      shipList.replaceChildren();
      if (!addresses.length) {
        const p = document.createElement("p");
        p.textContent = "No saved addresses yet.";
        shipList.appendChild(p);
        return;
      }
      addresses.forEach((addr, i) => {
        const label = document.createElement("label");
        label.className = "ws-checkout-field";
        label.style.display = "flex";
        label.style.flexDirection = "row";
        label.style.alignItems = "flex-start";
        label.style.gap = "10px";
        label.style.cursor = "pointer";
        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = "deliveryAddress";
        radio.value = addr.id;
        radio.style.marginTop = "3px";
        if (!isPickup && i === 0) {
          radio.checked = true;
          shipSelectedId = addr.id;
          activeType = "Ship";
          submit.disabled = false;
        }
        radio.addEventListener("change", () => {
          shipSelectedId = addr.id;
          activeType = "Ship";
          submit.disabled = false;
        });
        label.appendChild(radio);
        const nameLine = [
          [addr.firstName, addr.lastName].filter(Boolean).join(" "),
          addr.isDefault ? "(Default)" : "",
        ]
          .filter(Boolean)
          .join(" ");
        label.appendChild(
          addressCard(nameLine, [
            [addr.address, addr.address2].filter(Boolean).join(", "),
            [addr.stateCode, addr.city].filter(Boolean).join(", "),
            [addr.countryCode, addr.zipCode].filter(Boolean).join("-"),
            addr.phone ? `Phone: ${addr.phone}` : "",
          ]),
        );
        shipList.appendChild(label);
      });
    })();

    (async () => {
      let locations = [];
      try {
        const res = await api.c7GetPickupLocations();
        locations = Array.isArray(res?.pickupLocations)
          ? res.pickupLocations
          : Array.isArray(res)
            ? res
            : [];
      } catch {
        /* leave empty */
      }
      pickupList.replaceChildren();
      if (!locations.length) {
        const p = document.createElement("p");
        p.textContent = "No pickup locations available.";
        pickupList.appendChild(p);
        return;
      }
      locations.forEach((loc, i) => {
        const label = document.createElement("label");
        label.className = "ws-checkout-field";
        label.style.display = "flex";
        label.style.flexDirection = "row";
        label.style.alignItems = "flex-start";
        label.style.gap = "10px";
        label.style.cursor = "pointer";
        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = "deliveryLocation";
        radio.value = loc.id;
        radio.style.marginTop = "3px";
        if (isPickup && i === 0) {
          radio.checked = true;
          pickupSelectedId = loc.id;
          activeType = "Pickup";
          submit.disabled = false;
        }
        radio.addEventListener("change", () => {
          pickupSelectedId = loc.id;
          activeType = "Pickup";
          submit.disabled = false;
        });
        label.appendChild(radio);
        label.appendChild(
          addressCard(loc.title || "", [
            [loc.address, loc.address2].filter(Boolean).join(", "),
            [loc.stateCode, loc.city].filter(Boolean).join(", "),
            [loc.countryCode, loc.zipCode].filter(Boolean).join("-"),
            loc.phone ? `Phone: ${loc.phone}` : "",
          ]),
        );
        pickupList.appendChild(label);
      });
    })();

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const payload =
        activeType === "Pickup"
          ? pickupSelectedId && { orderDeliveryMethod: "Pickup", pickupInventoryLocationId: pickupSelectedId }
          : shipSelectedId && {
              orderDeliveryMethod: "Ship",
              shipToCustomerAddressId: shipSelectedId,
              billToCustomerAddressId: shipSelectedId,
            };
      if (!payload) return;
      submit.disabled = true;
      emit("ws-membership-update", { membershipId: membership.id, membership, payload });
    });

    return form;
  }

  _renderPayment(membership) {
    const form = document.createElement("form");
    form.className = "ws-checkout-form";
    const { sec, fields } = section("Update payment method");
    const list = document.createElement("div");
    fields.appendChild(list);
    form.appendChild(sec);
    const { footer, submit } = submitFooter("Save");
    submit.disabled = true;
    form.appendChild(footer);

    let selectedId = null;

    (async () => {
      const customerId = getState().user?.customerId || "";
      let savedCards = [];
      try {
        const res = await api.c7GetSavedCards(customerId);
        savedCards = Array.isArray(res?.savedCards) ? res.savedCards : [];
      } catch {
        /* leave empty */
      }
      list.replaceChildren();
      if (!savedCards.length) {
        const p = document.createElement("p");
        p.textContent = "No saved cards yet.";
        list.appendChild(p);
        return;
      }
      savedCards.forEach((card, i) => {
        const label = document.createElement("label");
        label.className = "ws-checkout-field";
        label.style.display = "flex";
        label.style.flexDirection = "row";
        label.style.gap = "10px";
        label.style.cursor = "pointer";
        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = "card";
        radio.value = card.id;
        if (i === 0 || card.isDefault) {
          radio.checked = true;
          selectedId = card.id;
          submit.disabled = false;
        }
        radio.addEventListener("change", () => {
          selectedId = card.id;
          submit.disabled = false;
        });
        label.appendChild(radio);
        const info = document.createElement("div");
        info.textContent = `${card.cardBrand || "Card"} ${card.maskedCardNumber || ""}`;
        label.appendChild(info);
        list.appendChild(label);
      });
    })();

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      if (!selectedId) return;
      submit.disabled = true;
      emit("ws-membership-update", {
        membershipId: membership.id,
        membership,
        payload: { customerCreditCardId: selectedId },
      });
    });

    return form;
  }

  _renderFrequency(membership) {
    const form = document.createElement("form");
    form.className = "ws-checkout-form";
    const { sec, fields } = section("Update frequency");

    const offered = membership.club?.subscription?.frequenciesOffered || [];
    const current = membership.subscription?.frequency;
    let selected = current;

    if (!offered.length) {
      const p = document.createElement("p");
      p.textContent = "No alternate frequencies available for this club.";
      fields.appendChild(p);
    }
    offered.forEach((freq, i) => {
      const label = document.createElement("label");
      label.className = "ws-checkout-field";
      label.style.display = "flex";
      label.style.flexDirection = "row";
      label.style.gap = "10px";
      label.style.cursor = "pointer";
      const radio = document.createElement("input");
      radio.type = "radio";
      radio.name = "frequency";
      radio.value = String(freq);
      if (freq === current || (!current && i === 0)) {
        radio.checked = true;
        selected = freq;
      }
      radio.addEventListener("change", () => {
        selected = freq;
      });
      label.appendChild(radio);
      const info = document.createElement("div");
      info.textContent = `Every ${freq} month${Number(freq) === 1 ? "" : "s"}`;
      label.appendChild(info);
      fields.appendChild(label);
    });

    form.appendChild(sec);
    const { footer, submit } = submitFooter("Save");
    submit.disabled = !offered.length;
    form.appendChild(footer);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      if (selected == null) return;
      submit.disabled = true;
      emit("ws-membership-update", {
        membershipId: membership.id,
        membership,
        payload: { frequency: selected },
      });
    });

    return form;
  }

  _renderCancel(membership) {
    const form = document.createElement("form");
    form.className = "ws-checkout-form";
    const { sec, fields } = section("Cancel membership");

    const reasonGroup = document.createElement("div");
    reasonGroup.className = "ws-checkout-field";
    const reasonLabel = document.createElement("label");
    reasonLabel.textContent = "Reason";
    reasonGroup.appendChild(reasonLabel);
    const reasonSelect = document.createElement("select");
    reasonSelect.className = "ws-checkout-input";
    CANCEL_REASONS.forEach((r) => {
      const opt = document.createElement("option");
      opt.value = r;
      opt.textContent = r;
      reasonSelect.appendChild(opt);
    });
    reasonGroup.appendChild(reasonSelect);
    fields.appendChild(reasonGroup);

    const commentsGroup = document.createElement("div");
    commentsGroup.className = "ws-checkout-field";
    const commentsLabel = document.createElement("label");
    commentsLabel.textContent = "Comments (optional)";
    commentsGroup.appendChild(commentsLabel);
    const comments = document.createElement("textarea");
    comments.className = "ws-checkout-input";
    comments.rows = 3;
    commentsGroup.appendChild(comments);
    fields.appendChild(commentsGroup);

    form.appendChild(sec);
    const { footer, submit } = submitFooter("Cancel Membership");
    form.appendChild(footer);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      submit.disabled = true;
      emit("ws-membership-cancel", {
        membershipId: membership.id,
        reason: reasonSelect.value,
        comments: comments.value.trim(),
      });
    });

    return form;
  }
}
